// The price in the walkthrough scenes is a smooth function of time: a few
// low-frequency sine waves plus, where the story needs a trend, a slow drift
// (a tanh ramp or a gaussian bump, both infinitely smooth). Events are found
// where that function crosses a level, never forced through points, so the
// line has no knees or flat spots.

export type Wave = [amplitude: number, period: number, phase: number];

/** A sum of sine waves. */
export const waves =
  (parts: Wave[]) =>
  (t: number): number =>
    parts.reduce(
      (sum, [a, period, phase]) =>
        sum + a * Math.sin((2 * Math.PI * t) / period + phase),
      0
    );

/** A smooth step from 0 to 1 centred at `at`, about `width` seconds wide. */
export const ramp = (t: number, at: number, width: number): number =>
  0.5 * (1 + Math.tanh((t - at) / (width / 2)));

/** A smooth bump of height 1 at `at`, about `width` seconds wide. */
export const bump = (t: number, at: number, width: number): number =>
  Math.exp(-(((t - at) / (width / 2)) ** 2));

export interface Crossing {
  t: number;
  dir: "up" | "down";
}

/** Where `p` crosses `level` between `from` and `to` (refined by bisection). */
export function crossings(
  p: (t: number) => number,
  level: number,
  from: number,
  to: number,
  dt = 0.01
): Crossing[] {
  const out: Crossing[] = [];
  let a = from;
  let fa = p(a) - level;
  for (let b = from + dt; b <= to + 1e-9; b += dt) {
    const fb = p(b) - level;
    if (fa === 0 || fa * fb < 0) {
      let lo = a;
      let hi = b;
      for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if ((p(mid) - level) * fa > 0) lo = mid;
        else hi = mid;
      }
      out.push({ t: (lo + hi) / 2, dir: fb > fa ? "up" : "down" });
    }
    a = b;
    fa = fb;
  }
  return out;
}

/** The first crossing of `level` in direction `dir` after `from`. */
export function firstCrossing(
  p: (t: number) => number,
  level: number,
  dir: Crossing["dir"],
  from: number,
  to: number
): number {
  const hit = crossings(p, level, from, to).find((c) => c.dir === dir);
  if (!hit)
    throw new Error(`The price never crosses ${level} ${dir} after ${from}`);
  return hit.t;
}

/**
 * How smooth the drawn line is: walks the curve in segments of `step` px (as
 * drawn, `px` px per second and `py` px per price unit) and returns the
 * largest turning angle between consecutive segments (degrees), and the
 * shortest run (px) between two changes of the bending direction.
 */
export function smoothness(
  p: (t: number) => number,
  from: number,
  to: number,
  px: number,
  py: number,
  step = 2
): { maxTurn: number; shortestRun: number } {
  // Points every `step` px along the curve's length.
  const pts: [number, number][] = [];
  let t = from;
  let last: [number, number] = [t * px, -p(t) * py];
  pts.push(last);
  while (t < to) {
    // Advance t until the point is `step` px away.
    let dt = step / px / 4;
    let next: [number, number] = last;
    for (;;) {
      const nt = t + dt;
      next = [nt * px, -p(nt) * py];
      if (Math.hypot(next[0] - last[0], next[1] - last[1]) >= step) {
        t = nt;
        break;
      }
      dt *= 1.5;
    }
    pts.push(next);
    last = next;
  }
  let maxTurn = 0;
  let shortestRun = Infinity;
  let sign = 0;
  let runStart = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const a = Math.atan2(pts[i][1] - pts[i - 1][1], pts[i][0] - pts[i - 1][0]);
    const b = Math.atan2(pts[i + 1][1] - pts[i][1], pts[i + 1][0] - pts[i][0]);
    let turn = b - a;
    while (turn > Math.PI) turn -= 2 * Math.PI;
    while (turn < -Math.PI) turn += 2 * Math.PI;
    const deg = Math.abs(turn) * (180 / Math.PI);
    maxTurn = Math.max(maxTurn, deg);
    // Ignore numerically flat stretches when tracking the bending direction.
    if (deg < 0.02) continue;
    const s = Math.sign(turn);
    if (sign !== 0 && s !== sign) {
      // A run between two flips (the first run, cut by the window, does not count).
      if (runStart > 0)
        shortestRun = Math.min(shortestRun, (i - runStart) * step);
      runStart = i;
    }
    sign = s;
  }
  return { maxTurn, shortestRun };
}
